import type { IPopupProps } from './Popup'
import type { ITOAPopupProps } from './Templates'

import { styled } from '@linaria/react'
import { ButtonSC } from 'atoms/Button'
import { FallbackImageSC } from 'atoms/FallbackImage'
import { PosterSC } from 'atoms/ImagePoster'
import { ButtonGroupWrapper } from 'blocks/ButtonGroup'
import { DescriptionStyled } from 'blocks/Description/Description.styled'
import { Wrapper } from 'blocks/TextBlock'
import { COLOR, CORNERS, FIXED_INDENTS, LAYOUT, MEDIA } from 'vars/index'
import {
  PopupAlignItems,
  PopupColumnGap,
  PopupDirection,
  PopupRowGap,
} from './Popup.h'

export const PopupOverlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  background: var(${COLOR.SURFACE_OPACITY_DARK_40});
  z-index: 10;
`

// FIXME: check variables with `|| 0`, what was meant by this?
// FIXME: in https://setplexapps.atlassian.net/browse/FP-2701
// for example:
//  `calc(var(${padding?.[0]}) - var(--scrollbarOffset))` || 0
//  ↓↓↓
//  `calc(var(${padding?.[0] || 0}) - var(--scrollbarOffset))`
//  or ↓↓↓
//  padding && padding[0] ? `calc(var(${padding[0]}) - var(--scrollbarOffset))` : 0
export const PopupWindow = styled.div<IPopupProps>`
  //  set scrollBarOffset variable and add all popupWindow padding equal to this to avoid  discrepancy with layouts
  --scrollbarOffset: 8px;
  --posterHeight: 292px;
  --paddingWithPoster: 24px;
  --paddingTop: ${({ padding }) =>
    padding?.[0] ? `calc(var(${padding[0]}) - var(--scrollbarOffset))` : `0`};
  --paddingTopWithPoster: calc(
    var(--posterHeight) + var(--paddingWithPoster) - var(--scrollbarOffset)
  );
  --paddingRight: ${({ padding }) =>
    padding?.[1] ? `calc(var(${padding[1]}) - var(--scrollbarOffset))` : `0`};
  --paddingBottom: ${({ padding }) =>
    padding?.[2] || padding?.[0]
      ? `calc(var(${padding?.[2] || padding?.[0]}) - var(--scrollbarOffset))`
      : `0`};
  --paddingLeft: ${({ padding }) =>
    padding?.[3] || padding?.[1]
      ? `calc(var(${padding?.[3] || padding?.[1]}) - var(--scrollbarOffset))`
      : `0`};

  width: calc(100vw - 32px);
  height: ${({ height }) => (height ? `${height}%` : 'auto')};
  max-height: 80vh;
  display: flex;
  flex-direction: ${({ direction }) => direction || PopupDirection.Column};
  align-items: ${({ alignItems }) => alignItems || PopupAlignItems.Stretch};
  row-gap: ${({ rowGap }) => `var(${rowGap})`};
  column-gap: ${({ columnGap }) =>
    `var(${columnGap || PopupColumnGap.INDENT_20})`};
  position: relative;
  padding: var(--scrollbarOffset);
  background: ${({ popupBackground }) => `var(${popupBackground})`};
  border-radius: var(${CORNERS.CORNERS_10});

  > ${PosterSC} {
    position: absolute;
    width: 100%;
    top: 0;
    left: 0;
  }

  ${MEDIA.MIN_600} {
    width: ${({ width }) => width ?? 'auto'};
  }
`

export const PopupGradientOverlay = styled.div<{
  isTopGradientShown: boolean
  isBottomGradientShown: boolean
}>`
  display: flex;
  flex: 1;
  min-height: 1px;
  position: relative;

  &:before,
  &:after {
    content: '';
    position: absolute;
    width: calc(100% - var(--scrollbarOffset));
    height: 4.8vh;
  }

  &:before {
    display: ${({ isTopGradientShown }) =>
      isTopGradientShown ? 'block' : 'none'};
    top: -1px;
    background: var(${COLOR.MAIN_GRADIENT_180});
  }

  &:after {
    display: ${({ isBottomGradientShown }) =>
      isBottomGradientShown ? 'block' : 'none'};
    bottom: -1px;
    background: var(${COLOR.MAIN_GRADIENT_0});
  }
`

export const PopupHeaderSC = styled.div<{
  background?: COLOR
  cornersHeader?: CORNERS
  withPosterUrl?: boolean
  notRelative?: boolean
}>`
  position: ${({ notRelative }) => (notRelative ? 'initial' : 'relative')};
  background: ${({ background }) => `var(${background})`};
  padding: ${({ withPosterUrl }) =>
    `${
      withPosterUrl
        ? 'var(--paddingTopWithPoster) var(--paddingRight) 0 var(--paddingLeft)'
        : 'var(--paddingTop) var(--paddingRight) 0 var(--paddingLeft)'
    }`};
  border-radius: ${({ cornersHeader }) => `var(${cornersHeader})`};

  > ${ButtonSC}[data-arcana-button='true'] {
    position: absolute;
    right: 0;
    top: 0;
    height: fit-content;
    z-index: 1;
  }
`

export const PopupFooterWrapperSC = styled.div`
  padding: var(${FIXED_INDENTS.INDENT_40}) var(--paddingRight)
    var(--paddingBottom) var(--paddingLeft);
`

export const PopupScrollableBlockWithGradient = styled.div<{
  rowGapDescription: PopupRowGap
}>`
  overflow: auto;
  overscroll-behavior-y: none;
  display: flex;
  flex-direction: column;
  padding: 0 var(--paddingRight) 0 var(--paddingLeft);
  row-gap: ${({ rowGapDescription }) =>
    `var(${rowGapDescription || PopupRowGap.INDENT_20})`};

  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(${COLOR.SURFACE_DARK_5});
    border-radius: 2px;
    cursor: pointer;
  }
`

export const TVShowPopupSC = styled.div<IPopupProps>`
  ${PopupGradientOverlay}:after {
    height: var(${FIXED_INDENTS.INDENT_140});
  }
  ${PopupGradientOverlay}:before {
    height: var(${FIXED_INDENTS.INDENT_140});
  }

  & ${PopupWindow} {
    width: 100vw;
    background: var(${COLOR.SURFACE_DARK_4});

    ${MEDIA.MAX_599} {
      height: 100%;
      max-height: 100%;
    }

    ${MEDIA.MIN_600} {
      width: ${({ width }) => width ?? 'auto'};
    }

    ${PopupHeaderSC} {
      padding-bottom: 24px;
    }

    ${PopupScrollableBlockWithGradient} {
      padding-top: 8px;
      padding-bottom: 8px;
    }
  }
`

export const MoviesPopupSC = styled.div<IPopupProps>`
  ${PopupGradientOverlay}:after {
    height: var(${FIXED_INDENTS.INDENT_140});
  }
  ${PopupGradientOverlay}:before {
    height: var(${FIXED_INDENTS.INDENT_140});
  }

  & ${PopupWindow} {
    width: 100vw;
    background: var(${COLOR.SURFACE_DARK_4});

    ${MEDIA.MIN_600} {
      width: ${({ width }) => width ?? 'auto'};
    }

    ${PopupHeaderSC} {
      padding-bottom: 24px;
    }

    ${PopupScrollableBlockWithGradient} {
      padding-top: 8px;
      padding-bottom: 8px;
    }
  }
`

export const TOAPopupSC = styled.div<ITOAPopupProps>`
  ${PopupWindow} {
    --scrollbarOffset: 0px;
    --paddingBottom: var(${FIXED_INDENTS.INDENT_60});
    --paddingRight: var(${FIXED_INDENTS.INDENT_60});
    --paddingLeft: var(${FIXED_INDENTS.INDENT_60});
    --paddingTop: var(${FIXED_INDENTS.INDENT_250});

    ${MEDIA.MIN_600} {
      max-height: 80vh;
      --paddingTop: var(${FIXED_INDENTS.INDENT_80});
    }

    ${MEDIA.MAX_599} {
      max-height: 100%;
      height: 100%;
      width: 100%;
    }

    ${MEDIA.MIN_1240} {
      --paddingTop: var(${FIXED_INDENTS.INDENT_140});
      --paddingBottom: var(${FIXED_INDENTS.INDENT_120});
      --paddingRight: var(${FIXED_INDENTS.INDENT_120});
      --paddingLeft: var(${FIXED_INDENTS.INDENT_120});
    }

    ${MEDIA.MAX_1239_LANDSCAPE} {
      --paddingBottom: var(${FIXED_INDENTS.INDENT_60});
      --paddingRight: var(${FIXED_INDENTS.INDENT_60});
      --paddingLeft: var(${FIXED_INDENTS.INDENT_60});
      --paddingTop: var(${FIXED_INDENTS.INDENT_250});
      max-height: 100%;
      height: 100%;
      width: 100%;
    }

    ${MEDIA.MIN_1640} {
      --paddingTop: var(${FIXED_INDENTS.INDENT_180});
      --paddingBottom: var(${FIXED_INDENTS.INDENT_160});
      --paddingRight: var(${FIXED_INDENTS.INDENT_160});
      --paddingLeft: var(${FIXED_INDENTS.INDENT_160});
    }
  }

  ${PopupHeaderSC} {
    padding-bottom: var(${FIXED_INDENTS.INDENT_10});
  }

  ${PopupScrollableBlockWithGradient} {
    padding-right: 0;
    margin-right: var(${FIXED_INDENTS.INDENT_10});

    ${Wrapper} {
      padding-right: var(${FIXED_INDENTS.INDENT_60});

      ${MEDIA.MIN_1240} {
        padding-right: var(${FIXED_INDENTS.INDENT_120});
      }

      ${MEDIA.MIN_1640} {
        padding-right: var(${FIXED_INDENTS.INDENT_160});
      }
    }
  }

  ${PopupGradientOverlay}:after {
    height: var(${FIXED_INDENTS.INDENT_140});
  }
  ${PopupGradientOverlay}:before {
    height: var(${FIXED_INDENTS.INDENT_140});
  }

  & ${PopupFooterWrapperSC} {
    padding-top: var(${FIXED_INDENTS.INDENT_40});

    & ${ButtonSC} {
      max-height: 48px;
    }

    ${ButtonGroupWrapper} {
      gap: var(${FIXED_INDENTS.INDENT_40});
    }
  }
`
export const LogoWrapperSC = styled.div<{
  maxWidth: number
  maxHeight: number
  channelPosterUrl?: string
  background?: COLOR
}>`
  --width: 159px;
  --height: 94px;

  background: ${({ background }) =>
    background
      ? `var(${background})`
      : `var(${COLOR.SURFACE_OPACITY_LIGHT_20})`};
  display: flex;
  width: var(--width);
  height: var(--height);

  > ${PosterSC} {
    max-width: ${({ maxWidth }) => `${maxWidth}%`};
    max-height: ${({ maxHeight }) => `${maxHeight}%`};
    object-fit: ${({ channelPosterUrl }) =>
      channelPosterUrl ? 'contain' : 'unset'};
    margin: auto;
    width: 100%;
    height: 100%;
  }
`

export const PopupTitle = styled.div`
  --posterWidth: 159px;
  --descriptionWidth: 433px;
  --descriptionTouchWidht: 281px;

  display: grid;
  column-gap: 16px;
  grid-template-columns: var(--posterWidth) var(--descriptionWidth);
  align-items: baseline;
  row-gap: var(${FIXED_INDENTS.INDENT_40});

  ${FallbackImageSC} {
    margin: auto;
    line-height: 0;
  }

  ${DescriptionStyled} {
    ${Wrapper} {
      max-width: 100%;
    }
  }

  ${MEDIA.MAX_767} {
    grid-template-columns: var(--posterWidth) var(--descriptionTouchWidht);
  }

  ${MEDIA.MAX_599} {
    display: flex;
    flex-direction: column;
  }

  ${MEDIA.MIN_600} {
    align-items: center;
    flex-direction: row;
  }
`
export const PopupScrollableBlockDefault = styled.div<{
  rowGapDescription: PopupRowGap
}>`
  row-gap: ${({ rowGapDescription }) =>
    `var(${rowGapDescription || PopupRowGap.INDENT_20})`};
  overflow: auto;

  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(${COLOR.SURFACE_DARK_5});
    border-radius: 2px;
    cursor: pointer;
  }
`

export const PurchasedPopupSC = styled.div<
  IPopupProps & { popupHeaderHeight: number }
>`
  --popupHeaderHeight: ${({ popupHeaderHeight }) => `${popupHeaderHeight}px`};
  --popupWidthMax767: 568px;

  ${PopupWindow} {
    padding: 0;
    row-gap: 0;
    width: ${({ width }) => `min(${width}, 100%)`};

    ${MEDIA.MAX_767} {
      width: var(--popupWidthMax767);
    }

    ${MEDIA.MAX_599} {
      height: 100%;
      max-height: 100%;
      overflow: hidden;
      width: 100%;
    }

    ${MEDIA.MAX_1239_LANDSCAPE} {
      ${MEDIA.TOUCH} {
        max-height: 100%;
        overflow: hidden;
      }
    }

    & ${PopupGradientOverlay} {
      width: 100%;
    }

    & ${PopupHeaderSC} {
      padding: var(${FIXED_INDENTS.INDENT_80}) var(${LAYOUT.HorizontalPaddings});
    }

    & ${PopupScrollableBlockDefault} {
      padding: var(${FIXED_INDENTS.INDENT_80}) var(${LAYOUT.HorizontalPaddings})
        var(${FIXED_INDENTS.INDENT_0});
      justify-content: center;
      align-items: center;

      ${MEDIA.MAX_599} {
        display: flex;
        height: calc(100% - var(--popupHeaderHeight));
      }
    }

    & ${PopupFooterWrapperSC} {
      padding: var(${FIXED_INDENTS.INDENT_20}) var(--paddingRight)
        var(--paddingBottom) var(--paddingLeft);
    }
  }
`
